/**
 * One source as a chip: its kind and name, whether it has been switched off or deleted, what the latest
 * evaluation found about it, and a cross while editable. A source the AI used that the answer key lacks is
 * offered as a pill that adds it.
 */

import { Plus } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import type { DatasetSourceState } from '@/types/simulation-dataset.types';
import { FINDING_CLASS, type ChipFinding } from '../evaluation/findings';
import { RemoveButton } from '../ui/remove-button';
import { SOURCE_KIND_LABEL, sourceStateNote, type SourceOption } from './source-kinds';

interface SourceChipProps {
	kind: string;
	value: string;
	state?: DatasetSourceState;
	finding?: ChipFinding | null;
	onRemove?: () => void;
}

export const SourceChip = ({ kind, value, state, finding, onRemove }: SourceChipProps) => {
	const note = sourceStateNote(state);
	const chip = (
		<span
			className={cn(
				'inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border bg-white pl-3 text-[13px] leading-4.5',
				finding ? FINDING_CLASS[finding.tone].border : 'border-gray-500',
				onRemove ? 'pr-1' : 'pr-3'
			)}
		>
			<span className='shrink-0 text-gray-800'>{kind}</span>
			<span className='truncate text-text-body'>{value}</span>
			{note && <span className='shrink-0 text-gray-700'>{note}</span>}
			{finding && <span className={cn('shrink-0', FINDING_CLASS[finding.tone].text)}>{finding.label}</span>}
			{onRemove && <RemoveButton label={`Remove ${kind.toLowerCase()} ${value}`} onClick={onRemove} />}
		</span>
	);
	if (!finding) return chip;
	return (
		<Tooltip>
			<TooltipTrigger asChild>{chip}</TooltipTrigger>
			<TooltipContent side='bottom' align='start' className='max-w-sm'>
				{finding.tip}
			</TooltipContent>
		</Tooltip>
	);
};

export const AddSourcePill = ({
	option,
	state,
	onAdd
}: {
	option: SourceOption;
	state?: DatasetSourceState;
	onAdd: () => void;
}) => {
	const note = sourceStateNote(state);
	return (
		<PillButton dashed onClick={onAdd}>
			<Plus className='size-3' />
			{SOURCE_KIND_LABEL[option.kind]} {option.name}
			{note && (
				<>
					{' '}
					<span className='text-gray-700'>{note}</span>
				</>
			)}
		</PillButton>
	);
};
